import {
  CHART_LIMITS,
  HEX_COLOUR_RE,
  REQUIRED_PALETTE_KEYS,
  type BaseChart,
  type ChartValidationIssue,
  type ChartValidationResult,
} from './chart.js';
import type { ManiaEngineTuning } from './engine/mania/core.js';
import { VARIANTS } from './gameTypes.js';
import {
  MANIA_LANE_COUNT,
  SAME_BEAT_EPSILON,
  isManiaLane,
  validateManiaHoldOverlaps,
  type ManiaLane,
  type ManiaNote,
} from './maniaNote.js';

/**
 * Y-position of the strum bar as a fraction of canvas height for
 * Guitar mode. Sits a touch lower than Piano's (0.85) because the
 * fretboard's perspective trapezoid widens at the bottom — a lower
 * strum bar gives the gems a longer visual approach without
 * sacrificing tap visibility.
 */
export const GUITAR_STRUM_BAR_Y_FRAC = 0.88;

/**
 * Gem height as a fraction of canvas height in Guitar's renderer.
 * Smaller than Piano's tile fraction: Guitar rewards tighter timing
 * (no piano-tiles min-spacing floor, denser charts), so a smaller
 * geometric perfect zone keeps the perfect verdict honest at the
 * speed the source mania charts assume.
 */
export const GUITAR_GEM_HEIGHT_FRAC = 0.10;

/**
 * Geometric perfect-zone span as a fraction of `approachMs` for
 * Guitar mode. Same derivation as Piano (gem height / strum-bar
 * fraction) — the engine treats overlap of the gem body with the
 * strum line as PERFECT, anything outside as a `good` (or miss for
 * keyboard input that happened too early). Materially tighter than
 * Piano's ~0.33 so guitar feels like Guitar Hero and not Piano Tiles.
 */
export const GUITAR_PERFECT_OVERLAP_FRAC =
  GUITAR_GEM_HEIGHT_FRAC / GUITAR_STRUM_BAR_Y_FRAC;

/**
 * Early-tap "good" window for keyboard input in Guitar, as a
 * fraction of `approachMs`. Tighter than Piano's 0.15 so a too-eager
 * strum doesn't latch onto a gem still high up the highway. Players
 * who strum before the gem is in a sensible window get an empty tap
 * (eventual auto-miss) instead of a free `good`.
 */
export const GUITAR_KEYBOARD_GOOD_EARLY_FRAC = 0.10;

/**
 * Early-tap "good" window for pointer input in Guitar, as a fraction
 * of `approachMs`. Mobile / pointer players physically have to hit
 * the gem in the lane, so the timing tier stays forgiving — same
 * 1.0 saturation as Piano so any visible gem in the lane resolves.
 */
export const GUITAR_POINTER_GOOD_EARLY_FRAC = 1.0;

/**
 * Late-tap "good" window for keyboard input in Guitar, as a fraction
 * of `approachMs`. After the gem's trailing edge crosses the strum
 * bar (perfect window ends) the player still has this slack to strum
 * for a `good` instead of an immediate miss — a small forgiveness
 * band for notes that visually just cleared the line.
 */
export const GUITAR_KEYBOARD_GOOD_LATE_FRAC = 0.06;

/**
 * Late-tap "good" window for pointer input in Guitar. Slightly wider
 * than keyboard because touch strums land a few ms after the visual
 * contact the player felt.
 */
export const GUITAR_POINTER_GOOD_LATE_FRAC = 0.08;

/**
 * Per-miss HP damage scaling for Guitar. Charts average ~2.5× Piano
 * note density, so miss damage is scaled inversely (1 / 2.5) to keep
 * total HP bleed over a song comparable. HP gain on hits is unchanged
 * so good stretches still recover health at the same rate — only the
 * per-miss penalty is lighter.
 */
export const GUITAR_MISS_DAMAGE_MULTIPLIER = 0.4;

/**
 * Bundle of Guitar-specific fractions the shared mania engine reads
 * at config-build time. Lives next to the underlying constants so
 * tracing where Guitar's geometric perfect zone comes from is one
 * hop, and so per-mode tuning stays out of the engine itself.
 */
export const GUITAR_ENGINE_TUNING: ManiaEngineTuning = {
  perfectOverlapFrac: GUITAR_PERFECT_OVERLAP_FRAC,
  keyboardGoodEarlyFrac: GUITAR_KEYBOARD_GOOD_EARLY_FRAC,
  pointerGoodEarlyFrac: GUITAR_POINTER_GOOD_EARLY_FRAC,
  keyboardGoodLateFrac: GUITAR_KEYBOARD_GOOD_LATE_FRAC,
  pointerGoodLateFrac: GUITAR_POINTER_GOOD_LATE_FRAC,
  missDamageMultiplier: GUITAR_MISS_DAMAGE_MULTIPLIER,
};

/**
 * Floor for {@link GuitarChart.approachMs}. Guitar permits faster
 * reading than Piano (200 ms) because the source mania charts assume
 * tighter scroll speeds and there's no piano-tiles min-spacing rule
 * keeping notes apart. 150 ms is roughly osu! AR ~10.5.
 */
export const GUITAR_APPROACH_MS_MIN = 150;
export const GUITAR_APPROACH_MS_MAX = 2500;

/**
 * Min/max bounds for the optional per-chart timing windows. Guitar's
 * floor is more aggressive than Piano's (5 ms) because tighter ms
 * windows are a core part of the mode's "harder than Piano"
 * positioning. Authored variants typically sit between 30 ms (perfect)
 * and 100 ms (good); a 3 ms floor leaves room for an extreme charts
 * to push past osu's OD10 if a mapper really wants to.
 */
export const GUITAR_TIMING_WINDOW_MIN_MS = 3;
export const GUITAR_TIMING_WINDOW_MAX_MS = 500;

/**
 * Hard cap on note count per Guitar chart. Higher than Piano's 3000
 * because mania charts at 6★+ can comfortably exceed 1500 notes in
 * the same audio window Piano caps at ~1500 (no min-spacing floor +
 * denser jacks/streams). 5000 leaves headroom for 7★ extreme charts
 * without letting a malformed chart wedge the engine.
 */
export const MAX_GUITAR_NOTES = 5000;

/**
 * 4-lane Guitar Hero-style fretboard chart. Same {@link ManiaNote}
 * shape as {@link import('./pianoChart.js').PianoChart} so both modes
 * run on the shared mania engine, but Guitar permits denser, faster,
 * tighter charts because it's positioned as the harder mode of the
 * two and its source charts come straight from osu!mania (no
 * piano-tiles min-spacing rule). Difficulty ramp comes from chart
 * density + tighter `approachMs` + tighter timing windows.
 */
export interface GuitarChart extends BaseChart {
  mode: 'guitar';
  notes: ManiaNote[];
  /**
   * Per-chart perfect timing window in milliseconds. When absent
   * the engine falls back to the global
   * {@link import('./gameTypes.js').PERFECT_WINDOW_MS}. Hard /
   * Extreme variants tighten this so harder maps demand more
   * precise timing.
   */
  perfectWindowMs?: number;
  /**
   * Per-chart good timing window in milliseconds. See
   * {@link GuitarChart.perfectWindowMs}.
   */
  goodWindowMs?: number;
  /**
   * How long a gem is visible before its hit beat. The renderer
   * sizes the spawn-to-strum-bar travel from this value; the engine
   * uses it to gate "early strum" handling. Wall-clock so muscle
   * memory transfers between songs at different BPMs (mirrors osu!
   * AR semantics).
   */
  approachMs: number;
}

export function validateGuitarChart(chart: GuitarChart): ChartValidationResult {
  const issues: ChartValidationIssue[] = [];

  validateBaseFields(chart, issues);

  if (!Array.isArray(chart.notes) || chart.notes.length === 0) {
    issues.push({ level: 'error', message: 'Guitar chart needs at least one note' });
  } else if (chart.notes.length > MAX_GUITAR_NOTES) {
    issues.push({
      level: 'error',
      message: `notes ${chart.notes.length} exceeds limit ${MAX_GUITAR_NOTES}`,
    });
  }

  if (
    !Number.isFinite(chart.approachMs) ||
    chart.approachMs < GUITAR_APPROACH_MS_MIN ||
    chart.approachMs > GUITAR_APPROACH_MS_MAX
  ) {
    issues.push({
      level: 'error',
      message: `approachMs ${chart.approachMs} must be in [${GUITAR_APPROACH_MS_MIN}, ${GUITAR_APPROACH_MS_MAX}] ms`,
    });
  }

  for (const field of ['perfectWindowMs', 'goodWindowMs'] as const) {
    const value = chart[field];
    if (value === undefined) {
      continue;
    }
    if (
      !Number.isFinite(value) ||
      value < GUITAR_TIMING_WINDOW_MIN_MS ||
      value > GUITAR_TIMING_WINDOW_MAX_MS
    ) {
      issues.push({
        level: 'error',
        message: `${field} ${value} must be in [${GUITAR_TIMING_WINDOW_MIN_MS}, ${GUITAR_TIMING_WINDOW_MAX_MS}] ms`,
      });
    }
  }
  if (
    chart.perfectWindowMs !== undefined &&
    chart.goodWindowMs !== undefined &&
    Number.isFinite(chart.perfectWindowMs) &&
    Number.isFinite(chart.goodWindowMs) &&
    chart.perfectWindowMs > chart.goodWindowMs
  ) {
    issues.push({
      level: 'error',
      message: `perfectWindowMs ${chart.perfectWindowMs} must be ≤ goodWindowMs ${chart.goodWindowMs}`,
    });
  }

  validateNotes(chart, issues);

  return {
    ok: !issues.some((i) => i.level === 'error'),
    issues,
  };
}

function validateBaseFields(chart: GuitarChart, issues: ChartValidationIssue[]): void {
  if (!chart.id) {
    issues.push({ level: 'error', message: 'Chart id is required' });
  }
  const title = chart.title ?? '';
  if (!title.trim()) {
    issues.push({ level: 'error', message: 'Chart title is required' });
  }
  if (title.length > CHART_LIMITS.maxTitleLength) {
    issues.push({
      level: 'error',
      message: `Title exceeds ${CHART_LIMITS.maxTitleLength} chars`,
    });
  }
  if ((chart.description ?? '').length > CHART_LIMITS.maxDescriptionLength) {
    issues.push({
      level: 'error',
      message: `Description exceeds ${CHART_LIMITS.maxDescriptionLength} chars`,
    });
  }
  if (chart.mapper !== undefined) {
    if (typeof chart.mapper !== 'string' || chart.mapper.trim().length === 0) {
      issues.push({
        level: 'error',
        message: 'mapper, when present, must be a non-empty string',
      });
    } else if (chart.mapper.length > CHART_LIMITS.maxMapperLength) {
      issues.push({
        level: 'error',
        message: `Mapper exceeds ${CHART_LIMITS.maxMapperLength} chars`,
      });
    }
  }
  if (!Number.isFinite(chart.bpm) || chart.bpm < 40 || chart.bpm > 500) {
    issues.push({
      level: 'error',
      message: `BPM ${chart.bpm} out of range (40-500)`,
    });
  }
  if (!Number.isFinite(chart.totalBeats) || chart.totalBeats <= 0) {
    issues.push({ level: 'error', message: 'totalBeats must be > 0' });
  }
  if (Number.isFinite(chart.totalBeats) && chart.totalBeats > CHART_LIMITS.maxTotalBeats) {
    issues.push({
      level: 'error',
      message: `totalBeats ${chart.totalBeats} exceeds limit ${CHART_LIMITS.maxTotalBeats}`,
    });
  }
  if (
    !Number.isInteger(chart.difficulty) ||
    chart.difficulty < 1 ||
    chart.difficulty > 5
  ) {
    issues.push({
      level: 'error',
      message: `difficulty ${chart.difficulty} must be integer 1-5`,
    });
  }
  if (!(VARIANTS as ReadonlyArray<string>).includes(chart.variant)) {
    issues.push({
      level: 'error',
      message: `variant ${chart.variant} must be one of ${VARIANTS.join(', ')}`,
    });
  }

  if (!chart.audio || typeof chart.audio.src !== 'string' || chart.audio.src.length === 0) {
    issues.push({ level: 'error', message: 'audio.src is required' });
  } else {
    if (!Number.isFinite(chart.audio.bpm) || chart.audio.bpm !== chart.bpm) {
      issues.push({
        level: 'error',
        message: `audio.bpm (${chart.audio.bpm}) must match chart.bpm (${chart.bpm})`,
      });
    }
    if (!Number.isFinite(chart.audio.offsetMs) || chart.audio.offsetMs < 0) {
      issues.push({ level: 'error', message: 'audio.offsetMs must be a non-negative number' });
    }
    if (!Number.isFinite(chart.audio.durationSec) || chart.audio.durationSec <= 0) {
      issues.push({ level: 'error', message: 'audio.durationSec must be > 0' });
    }
    if ((chart.audio.onsets?.length ?? 0) > CHART_LIMITS.maxOnsets) {
      issues.push({
        level: 'error',
        message: `audio.onsets ${chart.audio.onsets.length} exceeds limit ${CHART_LIMITS.maxOnsets}`,
      });
    }
  }

  for (const key of REQUIRED_PALETTE_KEYS) {
    const value = chart.palette?.[key];
    if (typeof value !== 'string' || !HEX_COLOUR_RE.test(value)) {
      issues.push({
        level: 'error',
        message: `palette.${key} must be a hex colour like #RRGGBB`,
      });
    }
  }
}

function validateNotes(chart: GuitarChart, issues: ChartValidationIssue[]): void {
  if (!Array.isArray(chart.notes)) {
    return;
  }
  const ids = new Set<string>();
  const sorted = [...chart.notes].sort((a, b) => a.beat - b.beat);

  // Guitar permits primary stacking on the same beat (drops the
  // piano-tiles "two notes can never be on the same beat" rule)
  // because real osu!mania charts routinely place jacks and chords
  // on identical beats across different lanes. The dual-partner
  // helper still exists for chord accents on a single source note,
  // but back-to-back primaries on the same beat are now legal as
  // long as their lanes differ.

  let prevBeat = -Infinity;
  // Track lanes occupied at the most recent beat so we still catch
  // duplicate-lane stacking — two notes in the same lane on the same
  // beat would be unplayable.
  let prevBeatLanes = new Set<ManiaLane>();

  for (const note of sorted) {
    if (!note.id) {
      issues.push({ level: 'error', message: 'Note missing id', beat: note.beat });
      continue;
    }
    if (ids.has(note.id)) {
      issues.push({ level: 'error', message: `Duplicate note id ${note.id}` });
    }
    ids.add(note.id);

    if (!Number.isFinite(note.beat) || note.beat < 0 || note.beat > chart.totalBeats) {
      issues.push({
        level: 'error',
        message: `Note ${note.id} at beat ${note.beat} outside [0, ${chart.totalBeats}]`,
        beat: note.beat,
      });
      continue;
    }
    if (!isManiaLane(note.lane)) {
      issues.push({
        level: 'error',
        message: `Note ${note.id} lane ${note.lane} must be 0..${MANIA_LANE_COUNT - 1}`,
        beat: note.beat,
      });
      continue;
    }

    if (note.dualPartnerLane !== undefined) {
      if (!isManiaLane(note.dualPartnerLane)) {
        issues.push({
          level: 'error',
          message: `Note ${note.id} dualPartnerLane ${note.dualPartnerLane} must be 0..${MANIA_LANE_COUNT - 1}`,
          beat: note.beat,
        });
      } else if (note.dualPartnerLane === note.lane) {
        issues.push({
          level: 'error',
          message: `Note ${note.id} dualPartnerLane must differ from lane ${note.lane}`,
          beat: note.beat,
        });
      }
    }

    if (note.holdEndBeat !== undefined) {
      if (
        !Number.isFinite(note.holdEndBeat) ||
        note.holdEndBeat <= note.beat ||
        note.holdEndBeat > chart.totalBeats
      ) {
        issues.push({
          level: 'error',
          message: `Note ${note.id} holdEndBeat ${note.holdEndBeat} must be in (${note.beat}, ${chart.totalBeats}]`,
          beat: note.beat,
        });
      }
    }

    const sameBeat = Math.abs(note.beat - prevBeat) < SAME_BEAT_EPSILON;
    if (sameBeat && prevBeatLanes.has(note.lane)) {
      issues.push({
        level: 'error',
        message: `Note ${note.id} duplicates a note in lane ${note.lane} at beat ${note.beat}`,
        beat: note.beat,
      });
    }
    if (
      sameBeat &&
      note.dualPartnerLane !== undefined &&
      prevBeatLanes.has(note.dualPartnerLane)
    ) {
      issues.push({
        level: 'error',
        message: `Note ${note.id} dualPartnerLane ${note.dualPartnerLane} collides with another note at beat ${note.beat}`,
        beat: note.beat,
      });
    }

    if (sameBeat) {
      prevBeatLanes.add(note.lane);
      if (note.dualPartnerLane !== undefined) {
        prevBeatLanes.add(note.dualPartnerLane);
      }
    } else {
      prevBeatLanes = new Set<ManiaLane>([note.lane]);
      if (note.dualPartnerLane !== undefined) {
        prevBeatLanes.add(note.dualPartnerLane);
      }
    }
    prevBeat = note.beat;
  }

  validateManiaHoldOverlaps(sorted, issues);
}

/**
 * Tolerance applied when validating that a guitar gem's beat lines
 * up with an audio onset. Mirrors Piano's 60 ms — mania imports
 * snap exactly to source hit-object timing so the buffer just
 * accounts for rounding when re-deriving beats from the source's
 * timing-point table.
 */
export const GUITAR_ONSET_TOLERANCE_MS = 60;
